
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { baseUrl } from "../Univ.js";


function IndentExpiryList() {
    const [pharmviewData, setPharmviewData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
const [lowStockLimit, setLowStockLimit] = useState("20");
const [expiryDaysLimit, setExpiryDaysLimit] = useState("20");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // ============================================================
    // DATE HELPERS
    // ============================================================

    const today = useMemo(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);

    const addDays = (date, days) => {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    };

    const parseExpiryDate = (value) => {
        if (!value) return null;

        // Handles YYYY-MM-DD and date strings containing a time.
        const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);

        if (!match) return null;

        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);

        const date = new Date(year, month - 1, day);

        // Reject invalid dates such as 2026-02-31.
        if (
            date.getFullYear() !== year ||
            date.getMonth() !== month - 1 ||
            date.getDate() !== day
        ) {
            return null;
        }

        date.setHours(0, 0, 0, 0);
        return date;
    };

    const formatDate = (date) => {
        if (!date) return "-";

        return date.toLocaleDateString("en-GB");
    };

    const getDaysToExpire = (expiryDate) => {
        return Math.round(
            (expiryDate.getTime() - today.getTime()) /
            (1000 * 60 * 60 * 24)
        );
    };

    // ============================================================
    // FETCH DATA ON PAGE LOAD
    // ============================================================

    const fetchPharmviewData = async () => {
        setIsLoading(true);
        setError("");

        try {
            const response = await axios.get(
                `${baseUrl}indent-pharm-search/?qty=gt0`
            );

            // Support either a direct array or a paginated response.
            const data = Array.isArray(response.data)
                ? response.data
                : response.data?.results ||
                  response.data?.data ||
                  response.data?.items ||
                  [];

            setPharmviewData(data);
        } catch (err) {
            console.error("Failed to fetch pharmacy stock:", err);
            setError("Unable to load pharmacy stock. Please try again.");
            setPharmviewData([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPharmviewData();
    }, []);

    // ============================================================
    // NORMALIZE STOCK DATA
    // ============================================================

    const stockRows = useMemo(() => {
        return pharmviewData.map((item) => {
            const product = item.product || {};
            const expiryDate = parseExpiryDate(product.expiry);

            const quantity = Number(item.qty ?? 0);

            return {
                ...item,
                id: item.id,

                product: product.product || "-",
                schedule: product.schedule || "-",
                batch: product.batch || "-",
                pack: product.pack || "-",
                hsn: product.hsn || "-",
                type: product.type || "-",
                expiry: product.expiry || "-",
                mrp: product.mrp ?? 0,
                pprice: product.pprice ?? 0,

                qty: quantity,
                expiryDateObj: expiryDate,
                daysToExpire: expiryDate
                    ? getDaysToExpire(expiryDate)
                    : null,
            };
        });
    }, [pharmviewData, today]);

    // ============================================================
    // ALERT 1: LOW STOCK
    // ============================================================

    const lowStockRows = useMemo(() => {
    const limit = Number(lowStockLimit || 0);

    return stockRows
        .filter((item) => item.qty < limit)
        .sort((a, b) => a.qty - b.qty);
}, [stockRows, lowStockLimit]);
    // ============================================================
    // ALERT 2: EXPIRING WITHIN 20 DAYS
    // Includes already-expired stock.
    // ============================================================

    const expiringSoonRows = useMemo(() => {
    const days = Math.max(0, Number(expiryDaysLimit || 0));
    const lastDate = addDays(today, days);

    return stockRows
        .filter((item) => {
            if (!item.expiryDateObj) return false;
            return item.expiryDateObj <= lastDate;
        })
        .sort((a, b) => a.expiryDateObj - b.expiryDateObj);
}, [stockRows, today, expiryDaysLimit]);
    // ============================================================
    // CUSTOM EXPIRY DATE RANGE
    // ============================================================

    const filteredExpiryRows = useMemo(() => {
        if (!startDate || !endDate) {
            return expiringSoonRows;
        }

        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        return stockRows
            .filter((item) => {
                if (!item.expiryDateObj) return false;

                return (
                    item.expiryDateObj >= start &&
                    item.expiryDateObj <= end
                );
            })
            .sort((a, b) => a.expiryDateObj - b.expiryDateObj);
    }, [stockRows, startDate, endDate, expiringSoonRows]);

    // ============================================================
    // DATE PICKER HANDLERS
    // ============================================================

    const handleStartDateChange = (date) => {
        setStartDate(date);

        if (endDate && date && date > endDate) {
            setEndDate(null);
        }
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
    };

    const clearDateFilter = () => {
        setStartDate(null);
        setEndDate(null);
    };

    // ============================================================
    // SHARED DATAGRID COLUMNS
    // ============================================================

const columns = useMemo(() => [
    {
        field: "id",
        headerName: "ID",
        width: 70,
    },
    {
        field: "dept",
        headerName: "Raised To",
        width: 120,
    },
    {
        field: "product",
        headerName: "Product",
        minWidth: 200,
        flex: 1,
    },
    {
        field: "schedule",
        headerName: "Schedule",
        width: 110,
    },
    {
        field: "batch",
        headerName: "Batch",
        width: 120,
    },
    {
        field: "pack",
        headerName: "Pack",
        width: 80,
    },
    {
        field: "qty",
        headerName: "Qty",
        width: 80,
        renderCell: (params) => {
            const qty = Number(params.value || 0);

            return (
                <span
                    style={{
                        background: qty <= 5 ? "#991b1b" : "#fee2e2",
                        color: qty <= 5 ? "#ffffff" : "#991b1b",
                        padding: "5px 12px",
                        borderRadius: 6,
                        fontWeight: 700,
                    }}
                >
                    {qty}
                </span>
            );
        },
    },
    {
        field: "expiry",
        headerName: "Expiry",
        width: 120,
        renderCell: (params) => (
            <span>
                {formatDate(params.row.expiryDateObj)}
            </span>
        ),
    },
    {
        field: "daysToExpire",
        headerName: "Expiry Alert",
        width: 145,
        renderCell: (params) => {
            const days = params.value;

            if (days == null) return "-";

            let background = "#dcfce7";
            let color = "#166534";
            let label = `${days} days`;

            if (days < 0) {
                background = "#991b1b";
                color = "#ffffff";
                label = `Expired ${Math.abs(days)}d`;
            } else if (days === 0) {
                background = "#dc2626";
                color = "#ffffff";
                label = "Expires today";
            } else if (days <= 7) {
                background = "#fecaca";
                color = "#991b1b";
            } else if (
                days <= Math.max(0, Number(expiryDaysLimit || 0))
            ) {
                background = "#ffedd5";
                color = "#9a3412";
            }

            return (
                <span
                    style={{
                        background,
                        color,
                        borderRadius: 6,
                        padding: "5px 9px",
                        fontWeight: 700,
                        minWidth: 100,
                        textAlign: "center",
                    }}
                >
                    {label}
                </span>
            );
        },
    },
    {
        field: "mrp",
        headerName: "MRP",
        width: 90,
    },
    {
        field: "pprice",
        headerName: "Pur. Price",
        width: 100,
    },
], [expiryDaysLimit]);

    // ============================================================
    // SUMMARY CARD
    // ============================================================

    const SummaryCard = ({
        title,
        count,
        description,
        background,
        color,
        icon,
    }) => (
        <div className="col-xl-6 col-md-6 mb-3">
            <div
                style={{
                    background,
                    color,
                    borderRadius: 14,
                    padding: 22,
                    minHeight: 125,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
                    border: `1px solid ${color}30`,
                }}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 600 }}>
                            {title}
                        </div>

                        <div style={{ fontSize: 34, fontWeight: 800 }}>
                            {count}
                        </div>

                        <div style={{ fontSize: 13 }}>
                            {description}
                        </div>
                    </div>

                    <div style={{ fontSize: 38 }}>
                        {icon}
                    </div>
                </div>
            </div>
        </div>
    );

    // ============================================================
    // PAGE
    // ============================================================

    return (
        <section className="container-fluid mt-4 mb-5">
            <div className="row">
                <div className="col-12">
                    <h3
                        className="text-center p-3 rounded"
                        style={{
                            color: "#ffffff",
                            background: "linear-gradient(90deg, #0f766e, #0891b2)",
                            fontWeight: 800,
                        }}
                    >
                        Pharmacy Stock & Expiry Dashboard
                    </h3>
                </div>
            </div>

{/* CONFIGURABLE ALERT CRITERIA */}
<div
    className="card mt-3 mb-4"
    style={{
        border: "1px solid #dbeafe",
        borderRadius: 12,
        boxShadow: "0 3px 10px rgba(15, 23, 42, 0.04)"
    }}
>
    <div className="card-body">
        <h5 className="fw-bold mb-3" style={{ color: "#1e3a8a" }}>
            <i className="fa-solid fa-sliders me-2"></i>
            Alert Criteria Settings
        </h5>

        <div className="row align-items-end">
            <div className="col-md-4 col-sm-6 mb-3">
                <label className="form-label fw-semibold">
                    Minimum Stock Quantity
                </label>

                <input
                    type="number"
                    min="0"
                    step="1"
                    className="form-control"
                    value={lowStockLimit}
                    onChange={(e) => setLowStockLimit(e.target.value)}
                    placeholder="Enter minimum stock"
                />

                <small className="text-muted">
                    Flag items with stock below this quantity.
                </small>
            </div>

            <div className="col-md-4 col-sm-6 mb-3">
                <label className="form-label fw-semibold">
                    Expiry Warning (Days)
                </label>

                <input
                    type="number"
                    min="0"
                    step="1"
                    className="form-control"
                    value={expiryDaysLimit}
                    onChange={(e) => setExpiryDaysLimit(e.target.value)}
                    placeholder="Enter expiry warning days"
                />

                <small className="text-muted">
                    Include expired items and items expiring within this period.
                </small>
            </div>

            <div className="col-md-4 mb-3">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                        setLowStockLimit("20");
                        setExpiryDaysLimit("20");
                    }}
                >
                    <i className="fa-solid fa-rotate-left me-2"></i>
                    Reset Defaults
                </button>
            </div>
        </div>
    </div>
</div>

            {error && (
                <div className="alert alert-danger">
                    {error}
                    <button
                        className="btn btn-sm btn-outline-danger ms-3"
                        type="button"
                        onClick={fetchPharmviewData}
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* ================================================== */}
            {/* SUMMARY CARDS */}
            {/* ================================================== */}

            <div className="row mt-3">
                <SummaryCard
                    title="LOW / MINIMUM STOCK"
                    count={lowStockRows.length}
                    description={`Items with quantity below ${Number(lowStockLimit || 0)}`}
                    background="#fff1f2"
                    color="#9f1239"
                    icon="⚠️"
                />

                <SummaryCard
                    title="EXPIRING / EXPIRED STOCK"
                    count={expiringSoonRows.length}
                    description={`Expired items and items expiring within ${Math.max(0, Number(expiryDaysLimit || 0))} days`}
                    background="#fff7ed"
                    color="#9a3412"
                    icon="⏳"
                />
            </div>

            {isLoading ? (
                <div className="text-center p-5">
                    <div
                        className="spinner-border text-success"
                        role="status"
                    />
                    <h5 className="mt-3">
                        Loading pharmacy stock...
                    </h5>
                </div>
            ) : (
                <>
                    {/* ========================================== */}
                    {/* LOW STOCK TABLE */}
                    {/* ========================================== */}

                    <div
                        className="mt-4 p-3 rounded"
                        style={{
                            background: "#fffafa",
                            border: "1px solid #fecdd3",
                        }}
                    >
                        <h5
    className="p-3 rounded"
    style={{
        color: "#ffffff",
        background: "#be123c",
        fontWeight: 700,
    }}
>
    ⚠️ Low / Minimum Stock (&lt; {Number(lowStockLimit || 0)})
    <span className="float-end">
        {lowStockRows.length} items
    </span>
</h5>

                        <div style={{ width: "100%" }}>
                            <DataGrid
                                rows={lowStockRows}
                                columns={columns}
                                getRowId={(row) => row.id}
                                slots={{ toolbar: GridToolbar }}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            page: 0,
                                            pageSize: 10,
                                        },
                                    },
                                }}
                                pageSizeOptions={[10, 25, 50, 100]}
                                autoHeight
                                disableRowSelectionOnClick
                                showToolbar
                            />
                        </div>
                    </div>

                    {/* ========================================== */}
                    {/* EXPIRING TABLE */}
                    {/* ========================================== */}

                    <div
                        className="mt-5 p-3 rounded"
                        style={{
                            background: "#fffdf7",
                            border: "1px solid #fed7aa",
                        }}
                    >
     <h5
    className="p-3 rounded"
    style={{
        color: "#ffffff",
        background: "#c2410c",
        fontWeight: 700,
    }}
>
    ⏳ Expiring / Expired Stock
    <span className="float-end">
        {filteredExpiryRows.length} items
    </span>
</h5>
                        {/* DATE FILTER */}
                        <div className="row align-items-end mb-3">
                            <div className="col-md-3 mb-2">
                                <label className="form-label fw-semibold">
                                    From Expiry Date
                                </label>

                                <DatePicker
                                    selected={startDate}
                                    onChange={handleStartDateChange}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    maxDate={endDate || undefined}
                                    placeholderText="From date"
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                />
                            </div>

                            <div className="col-md-3 mb-2">
                                <label className="form-label fw-semibold">
                                    To Expiry Date
                                </label>

                                <DatePicker
                                    selected={endDate}
                                    onChange={handleEndDateChange}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate || undefined}
                                    placeholderText="To date"
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control"
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                />
                            </div>

                            <div className="col-md-3 mb-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={clearDateFilter}
                                >
                                    Clear Date Filter
                                </button>
                            </div>

                            <div className="col-md-3 mb-2 text-md-end">
                                <span
    className="badge p-2"
    style={{
        background: "#ffedd5",
        color: "#9a3412",
        fontSize: 13,
    }}
>
    Default: next {Math.max(0, Number(expiryDaysLimit || 0))} days
</span>
                            </div>
                        </div>

                        <div style={{ width: "100%" }}>
                            <DataGrid
                                rows={filteredExpiryRows}
                                columns={columns}
                                getRowId={(row) => row.id}
                                slots={{ toolbar: GridToolbar }}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            page: 0,
                                            pageSize: 10,
                                        },
                                    },
                                }}
                                pageSizeOptions={[10, 25, 50, 100]}
                                autoHeight
                                disableRowSelectionOnClick
                                showToolbar
                            />
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}

export default IndentExpiryList;
